import * as React from 'react';
import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import CheckoutAdditionalService from '../..';
import { useTheme } from '../../../theme';
import { getMilesForOrder, getIsFFPUsed } from '../../../Checkout/store/order/selectors';
import icons from '../../icons';
import { useConfig } from '../../../context';
import LoyaltyConnect, { ConnectType } from '../../../Loyalty/components/LoyaltyStub/LoyaltyConnect/LoyaltyConnect';
import { GetAccountInfoQuery } from '@websky/graphql';
import { getUserInfo } from '../../../Checkout/utils';
import { getI18nCountVariation } from '../../../utils';
import { useConfigRedirects } from '../../../hooks';

interface Props {
	userInfo?: GetAccountInfoQuery['CurrentUser'];
	refetchUser?: () => void;
	aggregationIcons?: React.ReactNode[];
	aggregationMode?: boolean;
	isServiceV2?: boolean;
}

const LoyaltyCard: React.FC<Props> = ({ userInfo, refetchUser, aggregationIcons, aggregationMode }) => {
	const { t } = useTranslation('Checkout');
	const { Loyalty: css } = useTheme('CheckoutAdditionalService');

	const [isLoyaltyConnect, setIsLoyaltyConnect] = useState(false);

	const isFFPProvidedToOrder = useSelector(getIsFFPUsed);
	const milesBonus = useSelector(getMilesForOrder);

	const {
		global: {
			companyInfo: { loyaltyProgramAccountUrl, loyaltyProgramName }
		}
	} = useConfig();

	const { onGoToAccountSettings } = useConfigRedirects();

	const openLoyaltyConnect = () => setIsLoyaltyConnect(true);
	const closeLoyaltyConnect = () => setIsLoyaltyConnect(false);

	const handleViewStatusClick = useCallback(() => {
		const loyaltyUrl = loyaltyProgramAccountUrl ?? '/account/loyalty';
		window.open(loyaltyUrl, '_blank');
	}, [isFFPProvidedToOrder]);

	const user = useMemo(() => getUserInfo(userInfo), [userInfo]);

	const onLoyaltyCardSaved = () => {
		closeLoyaltyConnect();
		refetchUser?.();
	};

	const loyaltyConnect = React.useMemo(() => {
		if (userInfo && !userInfo.userFfpInfo) {
			return { addButtonText: 'Register', type: ConnectType.Create };
		} else {
			return { addButtonText: 'Log in', type: ConnectType.Login };
		}
	}, [userInfo]);

	return (
		<>
			<CheckoutAdditionalService
				icon={icons.ffpIcon}
				className={css.loyalty}
				header={t(loyaltyProgramName)}
				description={t('Start earning miles now to flight cheaper')}
				addButtonText={t(loyaltyConnect.addButtonText)}
				isSelected={!!userInfo?.userFfpInfo}
				addClick={openLoyaltyConnect}
				buttons={[{ text: t('View status'), action: handleViewStatusClick }]}
				aggregationIcons={aggregationIcons}
				mode={aggregationMode ? 'aggregator' : 'default'}
			>
				{milesBonus > 0 ? (
					<>
						<p className={css.loyalty__awardedInfo}>
							{t('{{number}} {{miles}} will be awarded', {
								number: milesBonus,
								miles: t(`miles_${getI18nCountVariation(milesBonus)}`)
							})}
						</p>
						<p>{t('Miles will be credited to your account after the flight.')}</p>
					</>
				) : (
					<p className={css.loyalty__awardedInfo}>{t('Miles are not credited for this flight.')}</p>
				)}
			</CheckoutAdditionalService>

			<LoyaltyConnect
				isOpen={isLoyaltyConnect}
				userInfo={user}
				type={loyaltyConnect.type}
				authType="authenticate"
				onLoyaltyCardSaved={onLoyaltyCardSaved}
				onClose={closeLoyaltyConnect}
				onGoToSettings={onGoToAccountSettings}
			/>
		</>
	);
};

export default LoyaltyCard;
